import _ from 'lodash';
import {
  INTENT_TYPES, SEGMENT_CONFIG
} from 'v2/common/constants/index';
import {
  isIndexInstrumentPresentInInstrumentGroup,
  isWeeklyExpiry
} from '../components/InstrumentDetails/indexInstrumentsChecker';
import { getQuantModes, hasCryptoOptionsInstrument } from '../configs/index';
import ApplicationDomainUtility from 'v3/common/utils/applicationDomain';

const { isCrypto } = ApplicationDomainUtility;

const getSanitizedIntents = (intents, instrumentGroups, maxInstrumentInGroup, isSimpleMode, prevIntents) => {
  const newIntents = intents.map((intent) => {
    const newIntent = _.cloneDeep(intent);
    if (newIntent.type !== INTENT_TYPES.OpenLeg) {
      return newIntent;
    }

    if (isSimpleMode && newIntent?.instrument?.strike?.multiplier) {
      newIntent.instrument.strike.multiplier = 0;
    }

    // if changed pair to single, then change instrument index to 0
    if (maxInstrumentInGroup === 1 && newIntent?.instrument?.instrumentIndex === 1) {
      newIntent.instrument.instrumentIndex = 0;
    }

    const { instrument: { instrumentIndex, type }, identifier: { label } } = newIntent;
    const isIndexInstrumentPresent = isIndexInstrumentPresentInInstrumentGroup(
      instrumentGroups, instrumentIndex
    );

    // index related stuffs
    if (isIndexInstrumentPresent) {
      const isEquityPresent = newIntent.instrument.type === SEGMENT_CONFIG.equity;
      if (isEquityPresent) {
        newIntent.instrument.type = SEGMENT_CONFIG.future;
      }
    }

    const currentInstrumentSelection = instrumentGroups.map((group) => group[instrumentIndex]);
    const isInstrumentGroupsUnchanged = _.isEqual(prevIntents[label]?.instrumentGroup, currentInstrumentSelection);
    const isSameInstrumentIndex = _.isEqual(prevIntents[label]?.instrumentIndex, instrumentIndex);
    const hasInstrumentChanged = !isInstrumentGroupsUnchanged || !isSameInstrumentIndex

    if (type === SEGMENT_CONFIG.option && hasInstrumentChanged) {
      newIntent.instrument.expiry = {
        ...newIntent.instrument.expiry,
        index: 1,
        ...(!isCrypto && {cycle: isWeeklyExpiry(instrumentGroups, instrumentIndex) ? 'weekly' : 'monthly'}),
      };
    }

    if (isCrypto) {
      const isEmptyGroup = _.isEmpty(_.compact(_.flattenDeep(instrumentGroups)));
      const isNonCryptoFuture = !isEmptyGroup
        && !hasCryptoOptionsInstrument(instrumentGroups, instrumentIndex)
        && instrumentIndex === newIntent.instrument.instrumentIndex;

      if (isNonCryptoFuture) {
        newIntent.instrument.type = SEGMENT_CONFIG.future;
      }
    }

    return newIntent;
  });

  return newIntents;
};

const getSanitizedCases = (cases, instrumentGroups, maxInstrumentInGroup, isSimpleMode, prevIntents) => {
  const newCases = isSimpleMode ? [_.get(cases, '0')] : cases;

  return newCases.map((caseConfig) => {
    const newCaseConfig = _.cloneDeep(caseConfig);
    const { entry: { intents }, adjustments } = newCaseConfig;
    newCaseConfig.entry.intents = getSanitizedIntents(
      intents, instrumentGroups, maxInstrumentInGroup, isSimpleMode, prevIntents
    );

    const newAdjustments = adjustments.map((adjustment) => {
      const newAdjustment = _.cloneDeep(adjustment);
      const { intents: adjustmentIntents } = newAdjustment;
      newAdjustment.intents = getSanitizedIntents(
        adjustmentIntents, instrumentGroups, maxInstrumentInGroup, isSimpleMode, prevIntents
      );
      return newAdjustment;
    });

    newCaseConfig.adjustments = newAdjustments;
    return newCaseConfig;
  });
};

const getUpdatedPipeConfigs = (pipeConfigs, instrumentGroups, maxInstrumentsInGroup, isSimpleMode, prevIntents) => {
  return pipeConfigs.map((pipeConfig) => {
    if (_.has(pipeConfig, 'config.legs')) {
      const { config: { legs } } = pipeConfig;
      return {
        ...pipeConfig,
        config: {
          ...pipeConfig.config,
          legs: getSanitizedIntents(
            legs, instrumentGroups, maxInstrumentsInGroup, isSimpleMode, prevIntents
          )
        }
      };
    }

    return pipeConfig;
  });
}

export const onChangeInstrumentGroups = ({
  instrumentGroups,
  cases,
  maxInstrumentsInGroup,
  uiConfigs,
  pipeConfigs,
  prevIntents
}) => {
  const simple = getQuantModes.simple.value;
  const isSimpleMode = _.get(uiConfigs, 'mode', simple) === simple;

  return {
    cases: getSanitizedCases(cases, instrumentGroups, maxInstrumentsInGroup, false, prevIntents),
    pipeConfigs: getUpdatedPipeConfigs(pipeConfigs, instrumentGroups, maxInstrumentsInGroup, isSimpleMode, prevIntents),
    instrumentGroups,
    maxInstrumentsInGroup,
    uiConfigs,
    isPremiumOrProFeaturesEnabled: true,
  };
};
